/**
 * Field Overlay Component
 *
 * Renders signing fields as draggable, resizable overlays on PDF pages
 * Uses simple absolute positioning since it's rendered inside the page wrapper
 */

import React, { useCallback, useState } from 'react';
import { FieldPlacement } from './hooks/useFieldPlacements';
import styles from './FieldOverlay.module.scss';

export interface FieldOverlayProps {
  pageNumber: number;
  pageWidth: number;
  pageHeight: number;
  fields: FieldPlacement[];
  selectedFieldId: string | null;
  onSelectField: (id: string | null) => void;
  onUpdateField: (id: string, updates: Partial<FieldPlacement>) => void;
  onDeleteField: (id: string) => void;
  zoom: number;
  readOnlyMode?: boolean; // For signing mode - disables drag/resize/delete
}

interface DragState {
  fieldId: string;
  startX: number;
  startY: number;
  startFieldX: number;
  startFieldY: number;
}

interface ResizeState {
  fieldId: string;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
  handle: 'nw' | 'ne' | 'sw' | 'se';
}

export const FieldOverlay: React.FC<FieldOverlayProps> = ({
  pageNumber,
  pageWidth,
  pageHeight,
  fields,
  selectedFieldId,
  onSelectField,
  onUpdateField,
  onDeleteField,
  zoom,
  readOnlyMode = false,
}) => {
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);

  // Get fields for this page
  const pageFields = fields.filter(f => f.page === pageNumber);

  /**
   * Handle field click for selection
   */
  const handleFieldClick = useCallback((e: React.MouseEvent, fieldId: string) => {
    e.stopPropagation();
    onSelectField(fieldId);
  }, [onSelectField]);

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback((e: React.MouseEvent, field: FieldPlacement) => {
    e.preventDefault();
    e.stopPropagation();

    setDragState({
      fieldId: field.id,
      startX: e.clientX,
      startY: e.clientY,
      startFieldX: field.x,
      startFieldY: field.y,
    });
  }, []);

  /**
   * Handle resize start
   */
  const handleResizeStart = useCallback((
    e: React.MouseEvent,
    field: FieldPlacement,
    handle: 'nw' | 'ne' | 'sw' | 'se'
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setResizeState({
      fieldId: field.id,
      startX: e.clientX,
      startY: e.clientY,
      startW: field.w,
      startH: field.h,
      handle,
    });
  }, []);

  /**
   * Handle mouse move for drag/resize
   */
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (dragState) {
      const deltaX = (e.clientX - dragState.startX) / zoom;
      const deltaY = (e.clientY - dragState.startY) / zoom;

      const newX = dragState.startFieldX + deltaX;
      const newY = dragState.startFieldY + deltaY;

      // Get field dimensions to constrain properly
      const field = fields.find(f => f.id === dragState.fieldId);
      if (!field) return;

      // Constrain to page bounds, accounting for field size
      const maxX = (pageWidth / zoom) - field.w;
      const maxY = (pageHeight / zoom) - field.h;
      const constrainedX = Math.max(0, Math.min(maxX, newX));
      const constrainedY = Math.max(0, Math.min(maxY, newY));

      onUpdateField(dragState.fieldId, {
        x: constrainedX,
        y: constrainedY,
      });
    } else if (resizeState) {
      const deltaX = (e.clientX - resizeState.startX) / zoom;
      const deltaY = (e.clientY - resizeState.startY) / zoom;

      let newW = resizeState.startW;
      let newH = resizeState.startH;

      // Apply delta based on handle
      if (resizeState.handle === 'se') {
        newW = resizeState.startW + deltaX;
        newH = resizeState.startH + deltaY;
      } else if (resizeState.handle === 'sw') {
        newW = resizeState.startW - deltaX;
        newH = resizeState.startH + deltaY;
      } else if (resizeState.handle === 'ne') {
        newW = resizeState.startW + deltaX;
        newH = resizeState.startH - deltaY;
      } else if (resizeState.handle === 'nw') {
        newW = resizeState.startW - deltaX;
        newH = resizeState.startH - deltaY;
      }

      // Constrain to minimum size and page bounds
      const maxW = (pageWidth / zoom);
      const maxH = (pageHeight / zoom);
      newW = Math.max(50, Math.min(maxW, newW));
      newH = Math.max(20, Math.min(maxH, newH));

      onUpdateField(resizeState.fieldId, {
        w: newW,
        h: newH,
      });
    }
  }, [dragState, resizeState, zoom, pageWidth, pageHeight, fields, onUpdateField]);

  /**
   * Handle mouse up to end drag/resize
   */
  const handleMouseUp = useCallback(() => {
    setDragState(null);
    setResizeState(null);
  }, []);

  // Add/remove mouse listeners for drag/resize
  React.useEffect(() => {
    if (dragState || resizeState) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, resizeState, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={overlayRef}
      className={styles.fieldLayer}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: `${pageWidth}px`,
        height: `${pageHeight}px`,
        pointerEvents: 'auto',
      }}
      onClick={(e) => {
        // Deselect when clicking overlay background (not a field)
        if (e.target === e.currentTarget) {
          onSelectField(null);
        }
      }}
    >
      {pageFields.map((field) => {
        const isSelected = field.id === selectedFieldId;

        // Use brand blue for signing mode, signer color for editing mode
        const brandBlue = '#006c90'; // Propdocs brand blue
        const color = readOnlyMode ? brandBlue : (field.data?.color || '#999999');

        // Format field type for label (signature → Signature, initials → Initials)
        const fieldTypeLabel = field.type.charAt(0).toUpperCase() + field.type.slice(1);

        // For date and text fields in signing mode, show the value if available
        const isDateField = field.type === 'date';
        const isTextField = field.type === 'text';
        const fieldValue = (isDateField || isTextField) && readOnlyMode && field.data?.value ? field.data.value : null;
        const label = fieldValue || field.data?.label || fieldTypeLabel;

        // Only show tooltip in editing mode, not signing mode
        const tooltip = readOnlyMode ? null : (field.data?.signerEmail || 'No signer assigned');

        // Convert PDF coordinates to screen coordinates by scaling with zoom
        const screenX = field.x * zoom;
        const screenY = field.y * zoom;
        const screenW = field.w * zoom;
        const screenH = field.h * zoom;

        // Check if field has signature image (for completed signing fields)
        const signatureImage = field.data?.signatureImage;
        const isCompleted = field.data?.completed;

        return (
          <div
            key={field.id}
            data-field-id={field.id}
            className={`${styles.field} ${isSelected ? styles.selected : ''} ${isCompleted ? styles.completed : ''} ${readOnlyMode ? styles.signingMode : ''} ${signatureImage ? styles.hasSignature : ''} ${isDateField && readOnlyMode ? styles.dateField : ''}`}
            style={{
              left: `${screenX}px`,
              top: `${screenY}px`,
              width: `${screenW}px`,
              height: `${screenH}px`,
              // Only apply custom colors in editing mode - signing mode uses CSS classes
              ...(!readOnlyMode && {
                borderColor: color,
                backgroundColor: signatureImage ? 'transparent' : `${color}20`,
              }),
              // Date fields in signing mode should not have pointer cursor
              ...(isDateField && readOnlyMode && {
                cursor: 'default',
              }),
            }}
            onClick={(e) => !isDateField && handleFieldClick(e, field.id)} // Disable click for date fields in signing mode
            onMouseDown={(e) => !readOnlyMode && handleDragStart(e, field)} // Disable drag in signing mode
            onMouseEnter={() => !readOnlyMode && setHoveredFieldId(field.id)} // Disable tooltip in signing mode
            onMouseLeave={() => setHoveredFieldId(null)}
          >
            {signatureImage ? (
              // Show signature image if available
              <img
                src={signatureImage}
                alt={label}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  objectPosition: 'left bottom',
                  pointerEvents: 'none',
                }}
              />
            ) : (isTextField || isDateField) && fieldValue ? (
              // Show text/date field value with bottom-left aligned styling
              <div
                className={styles.textFieldValue}
                style={{
                  fontSize: `${Math.max(8, Math.min(16, (isDateField ? 14 : 12) * zoom))}px`,
                  lineHeight: `${Math.max(10, Math.min(20, (isDateField ? 16 : 14) * zoom))}px`
                }}
              >
                {fieldValue}
              </div>
            ) : (
              // Show label for empty fields or signature/date fields
              <div
                className={styles.fieldLabel}
                style={{
                  // Only apply color in editing mode - signing mode uses white from CSS
                  ...(!readOnlyMode && { color }),
                  fontSize: `${Math.max(5, Math.min(14, 11 * zoom))}px`
                }}
              >
                {label}
              </div>
            )}

            {/* Custom tooltip - shows instantly on hover (only in editing mode) */}
            {hoveredFieldId === field.id && tooltip && !readOnlyMode && (
              <div className={styles.tooltip}>
                {tooltip}
              </div>
            )}

            {/* Delete and resize controls - only in editing mode */}
            {isSelected && !readOnlyMode && (
              <>
                {/* Delete button - X without center dot */}
                <button
                  className={styles.deleteButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteField(field.id);
                  }}
                >
                  ✕
                </button>

                {/* Resize handles - 3 corners (nw, sw, se) - top-right has delete button */}
                {(field.type === 'signature' || field.type === 'text') && (
                  <>
                    <div
                      className={`${styles.resizeHandle} ${styles.nw}`}
                      onMouseDown={(e) => handleResizeStart(e, field, 'nw')}
                    />
                    <div
                      className={`${styles.resizeHandle} ${styles.sw}`}
                      onMouseDown={(e) => handleResizeStart(e, field, 'sw')}
                    />
                    <div
                      className={`${styles.resizeHandle} ${styles.se}`}
                      onMouseDown={(e) => handleResizeStart(e, field, 'se')}
                    />
                  </>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
